import React, { useEffect, useState } from 'react';
import { getUsers, blockUser, unblockUser, getSecurityPolicy } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import HoverCard from '../HoverCard';
import UserFormModal from './UserFormModal';
import LoginHistoryModal from './LoginHistoryModal';
import RobotActivityModal from './RobotActivityModal';
import Button from '../ui/Button';
import {
  ROLES, formatDateTime, badge, td, th, theadRow, muted, linkBtn, error as errorStyle,
} from './userStyles';

/**
 * Gestion des utilisateurs du dashboard (DO-208, ADMIN uniquement).
 * - nom : survol = email + notes, clic = fiche d'édition ;
 * - dernière connexion : survol = 5 dernières connexions réussies, clic = journal
 *   paginé de l'utilisateur ;
 * - « Journal de connexion » : journal global, filtrable.
 * DO-207 : colonne MFA (humains) ; robots : IP autorisées au survol, « Activité » =
 * journal de chaque appel d'API (global : « Activité des robots »).
 */
export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);   // null | { } (création) | utilisateur
  const [history, setHistory] = useState(null);   // null | { user } (null = journal global)
  const [activity, setActivity] = useState(null); // null | { user } (null = tous les robots)
  const [policy, setPolicy] = useState(null);     // réglages de sécurité effectifs (DO-207)

  const load = () => getUsers().then(setUsers).catch(() => setError('Chargement des utilisateurs impossible.'));
  useEffect(() => {
    load();
    getSecurityPolicy().then(setPolicy).catch(() => {});
  }, []);

  const toggleBlock = async u => {
    setError('');
    try {
      await (u.enabled ? blockUser(u.id) : unblockUser(u.id));
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Action impossible.');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0 }}>Utilisateurs</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button onClick={() => setHistory({ user: null })}>📜 Journal de connexion</Button>
          <Button onClick={() => setActivity({ user: null })}>🤖 Activité des robots</Button>
          <Button variant="primary" onClick={() => setEditing({})}>+ Nouvel utilisateur</Button>
        </div>
      </div>
      {error && <div style={{ ...errorStyle, margin: '0 0 1rem' }}>{error}</div>}

      <div style={{ background: '#ffffff', borderRadius: '12px', overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: '900px', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={theadRow}>
              {['Utilisateur', 'Rôle', 'Statut', 'MFA', 'Création', 'Dernière connexion', 'Actions'].map(h =>
                <th key={h} style={th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const [roleLabel, roleBg, roleColor] = ROLES[u.role] || [u.role, '#ececec', '#6b7280'];
              const isMe = u.username === me?.username;
              return (
                <tr key={u.id} style={{ borderBottom: '1px solid #ececec', ...(u.enabled ? {} : { opacity: 0.6 }) }}
                    className="ui-row">
                  <td style={{ ...td, fontWeight: 600 }}>
                    <HoverCard content={<UserCard user={u} policy={policy} />}>
                      <button onClick={() => setEditing(u)} style={{ ...linkBtn, fontWeight: 600 }}>
                        {u.username}{isMe && <span style={{ color: '#8a8f99', fontWeight: 400 }}> (vous)</span>}
                      </button>
                    </HoverCard>
                  </td>
                  <td style={td}><span style={badge(roleBg, roleColor)}>{roleLabel}</span></td>
                  <td style={td}>
                    {u.enabled
                      ? <span style={badge('#e2f3e8', '#1a7f37')}>Actif</span>
                      : <span style={badge('#fdecea', '#b3261e')}>Bloqué</span>}
                  </td>
                  <td style={td}>
                    {u.role === 'PUSH_AGENT'
                      ? <span style={muted}>clé</span>
                      : u.mfaEnabled
                        ? <span style={badge('#e2f3e8', '#1a7f37')} title={`Activé le ${formatDateTime(u.mfaEnabledAt)}`}>Activé</span>
                        : <span style={badge('#fff4e0', '#955d00')}
                                title={policy?.mfaRequired ? 'Enrôlement demandé à la prochaine connexion' : 'MFA non exigé'}>
                            Non activé
                          </span>}
                  </td>
                  <td style={{ ...td, color: '#6b7280', fontSize: '0.85rem' }}>{formatDateTime(u.createdAt)}</td>
                  <td style={{ ...td, fontSize: '0.85rem' }}>
                    {u.lastLoginAt ? (
                      <HoverCard content={<RecentLogins user={u} />}>
                        <button onClick={() => setHistory({ user: u })} style={linkBtn}>
                          {formatDateTime(u.lastLoginAt)}
                        </button>
                      </HoverCard>
                    ) : (
                      <button onClick={() => setHistory({ user: u })} style={{ ...linkBtn, ...muted }}>
                        Jamais connecté
                      </button>
                    )}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {u.role === 'PUSH_AGENT' && (
                      <Button size="sm" onClick={() => setActivity({ user: u })} style={{ marginRight: '0.5rem' }}>
                        📈 Activité
                      </Button>
                    )}
                    {!isMe && (u.enabled
                      ? <Button size="sm" variant="danger" onClick={() => toggleBlock(u)}>Bloquer</Button>
                      : <Button size="sm" variant="success" onClick={() => toggleBlock(u)}>Débloquer</Button>)}
                  </td>
                </tr>
              );
            })}
            {users.length === 0 && (
              <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#8a8f99', padding: '3rem' }}>
                Aucun utilisateur
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <UserFormModal
          user={editing.id ? editing : null}
          isMe={editing.username === me?.username}
          policy={policy}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}
      {history && <LoginHistoryModal user={history.user} onClose={() => setHistory(null)} />}
      {activity && (
        <RobotActivityModal user={activity.user} retentionDays={policy?.robotRequestsRetentionDays}
                            onClose={() => setActivity(null)} />
      )}
    </div>
  );
}

function UserCard({ user, policy }) {
  const globalIps = policy?.robotAllowedIps?.length ? policy.robotAllowedIps.join(', ') : null;
  return (
    <>
      {user.role === 'PUSH_AGENT' ? (
        <>
          <div style={muted}>Compte robot — authentification par clé</div>
          <div style={{ marginTop: '0.35rem' }}>
            <b>IP autorisées :</b>{' '}
            {user.allowedIps
              || (globalIps ? <span>{globalIps} <span style={muted}>(liste par défaut)</span></span>
                            : <span style={muted}>aucune restriction</span>)}
          </div>
          {user.lastCallIp && (
            <div><b>Dernier appel :</b> {user.lastCallIp} — {formatDateTime(user.lastCallAt)}
              {user.lastCallStatus >= 400 && <span style={{ color: '#b3261e' }}> (refusé, {user.lastCallStatus})</span>}
            </div>
          )}
        </>
      ) : <div><b>Email :</b> {user.email || <span style={muted}>non renseigné</span>}</div>}
      <div style={{ marginTop: '0.35rem' }}><b>Notes :</b></div>
      <div>{user.notes || <span style={muted}>aucune</span>}</div>
    </>
  );
}

function RecentLogins({ user }) {
  const logins = user.lastLogins || [];
  return (
    <>
      <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>5 dernières connexions</div>
      {logins.length === 0
        // Robot : les connexions de plus de 30 jours sont purgées, lastLoginAt reste
        ? <div style={muted}>Aucune connexion journalisée</div>
        : logins.map(t => <div key={t}>{formatDateTime(t)}</div>)}
      <div style={{ ...muted, marginTop: '0.35rem', fontSize: '0.75rem' }}>Cliquer pour l'historique complet</div>
    </>
  );
}
